import { useState, useEffect, useRef } from 'react';

interface HeroProps {
  onOpenWaitlist: () => void;
}

interface DropdownState {
  propertyType: boolean;
  designStyle: boolean;
  budget: boolean;
  area: boolean;
}

export default function Hero({ onOpenWaitlist }: HeroProps) {
  const [headline, setHeadline] = useState("From messy portfolios to one client-facing link.");
  const [subheadline, setSubheadline] = useState("Stop re-formatting your work for clients and platforms. Turn your Canva links, PDFs, Videos, Images, Know-hows into a single, high-end portfolio link in 60 seconds.");
  const [dropdowns, setDropdowns] = useState<DropdownState>({
    propertyType: false,
    designStyle: false,
    budget: false,
    area: false
  });
  const [selectedFilters, setSelectedFilters] = useState({
    propertyType: 'Property Type',
    designStyle: 'Design Style',
    budget: 'Budget',
    area: 'Area'
  });
  const [currentLeadIndex, setCurrentLeadIndex] = useState(0);
  const [badgesVisible, setBadgesVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const leads = [
    { name: 'Rahul', action: 'wants a consultation' },
    { name: 'Priya', action: 'requested a quote' },
    { name: 'Amit', action: 'booked a site visit' },
    { name: 'Sneha', action: 'sent an inquiry' },
    { name: 'Vikram', action: 'wants to discuss project' }
  ];

  const filterOptions = {
    propertyType: ['All Types', 'Apartment', 'Villa', 'Penthouse', 'Office', 'Retail'],
    designStyle: ['All Styles', 'Modern', 'Contemporary', 'Traditional', 'Minimalist', 'Luxury'],
    budget: ['All Budgets', '₹10-25 Lakhs', '₹25-50 Lakhs', '₹50L-1 Cr', '₹1 Cr+'],
    area: ['All Areas', '500-1000 sqft', '1000-2000 sqft', '2000-3000 sqft', '3000+ sqft']
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    
    if (utmSource === 'single_link') {
      setHeadline("From messy portfolios to one client-facing link.");
      setSubheadline("Stop re-formatting your work for clients and platforms. Turn your Canva links, PDFs, Videos, Images, Know-hows into a single, high-end portfolio link in 60 seconds.");
    } else if (utmSource === 'formats') {
      setHeadline("Reels, Canva, & PDFs—Finally in one organized portfolio");
      setSubheadline("Your work is in too many different file types to ever customize for a client. Sync all your scattered project write-ups, videos, and images into one professional link.");
    } else if (utmSource === 'algorithm') {
      setHeadline("Stop Performing for Algorithms. Start Presenting to Clients.");
      setSubheadline("Instagram is for reach; your portfolio is for revenue. Build a permanent, professional home that belongs to you—algorithm-free, distraction-free, and always online.");
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLeadIndex((prev) => (prev + 1) % leads.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [leads.length]);

  useEffect(() => {
    // Trigger badge animations after component mounts
    const timer = setTimeout(() => {
      setBadgesVisible(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-scroll animation for portfolio content - pauses on hover
    const scrollInterval = setInterval(() => {
      if (!isHovering) {
        setScrollPosition(prev => {
          const maxScroll = 200;
          if (prev >= maxScroll) {
            return 0;
          }
          return prev + 0.5;
        });
      }
    }, 50);
    return () => clearInterval(scrollInterval);
  }, [isHovering]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdowns({ propertyType: false, designStyle: false, budget: false, area: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (key: keyof DropdownState) => {
    setDropdowns(prev => ({
      propertyType: false,
      designStyle: false,
      budget: false,
      area: false,
      [key]: !prev[key]
    }));
  };

  const selectOption = (key: keyof typeof selectedFilters, value: string) => {
    setSelectedFilters(prev => ({ ...prev, [key]: value }));
    setDropdowns(prev => ({ ...prev, [key]: false }));
  };

  return (
    <section className="relative bg-gradient-to-br from-[#F8FCFE] via-white to-[#F0FAFD] overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#35C5F0]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#35C5F0]/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="text-left order-1">
            {/* Target Audience Tags */}
            <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
              {['Interior Designers', 'Architects', 'Construction Companies', 'Renovation Companies'].map((tag) => (
                <span key={tag} className="px-2 sm:px-3 py-1 bg-[#35C5F0]/10 border border-[#35C5F0]/30 rounded-full text-[10px] sm:text-xs text-[#35C5F0] font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#2F3438] mb-4 sm:mb-6 leading-tight">
              {headline}
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg lg:text-xl text-[#6B7280] mb-6 sm:mb-8 leading-relaxed">
              {subheadline}
            </p>

            {/* Format Support Badges */}
            <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-full shadow-sm">
                <i className="ri-video-fill text-red-500"></i>
                <span className="text-xs font-medium text-[#2F3438]">Videos</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-full shadow-sm">
                <i className="ri-image-fill text-green-500"></i>
                <span className="text-xs font-medium text-[#2F3438]">Photos</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-full shadow-sm">
                <i className="ri-file-pdf-fill text-orange-500"></i>
                <span className="text-xs font-medium text-[#2F3438]">Home Tours</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-full shadow-sm">
                <i className="ri-article-fill text-[#35C5F0]"></i>
                <span className="text-xs font-medium text-[#2F3438]">Know How</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
              <button 
                onClick={onOpenWaitlist}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-[#35C5F0] text-white rounded-xl font-semibold text-base sm:text-lg hover:bg-[#2BB5E0] transition-all shadow-lg shadow-[#35C5F0]/30 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-magic-fill"></i>
                Get Started for Free
              </button>
            </div>

            {/* Trust Indicator */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 text-[#6B7280]">
              <div className="flex -space-x-2 flex-shrink-0">
                <img
                  src="https://public.readdy.ai/ai/img_res/8a11863eafb6ed5e0d972fd16ff4485c.jpg"
                  alt="Designer"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover shadow-md"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20indian%20female%20architect%20headshot%20portrait%20studio%20lighting%20confident%20smile%20elegant%20saree%20formal&width=40&height=40&seq=hero-trust-female-2&orientation=squarish"
                  alt="Designer"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover shadow-md"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20indian%20male%20architect%20headshot%20portrait%20studio%20lighting%20confident%20smile%20glasses%20modern%20suit&width=40&height=40&seq=hero-trust-male-3&orientation=squarish"
                  alt="Designer"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover shadow-md"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20indian%20female%20interior%20designer%20headshot%20portrait%20studio%20lighting%20warm%20smile%20kurta%20modern&width=40&height=40&seq=hero-trust-female-4&orientation=squarish"
                  alt="Designer"
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-cover shadow-md"
                />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
                <span className="text-xs sm:text-sm">
                  Trusted by 500+ designers in Mumbai, Delhi, Bangalore
                </span>
              </div>
            </div>
          </div>

          {/* Right - Mobile Phone with Portfolio and Lady */}
          <div className="relative order-2 mt-8 lg:mt-0 flex justify-center lg:justify-end items-end min-h-[520px] lg:min-h-[580px]">
            <div className="absolute top-1/2 right-1/3 -translate-y-1/2 w-80 h-80 bg-[#00BFF3]/20 blur-[80px] rounded-full"></div>

            {/* Badges */}
            <div className="absolute top-12 left-12 sm:left-16 lg:left-20 z-30 flex flex-col gap-2">
              <div
                className={`bg-white/90 backdrop-blur-sm rounded-xl shadow-lg px-3 py-2 border border-[#E5E7EB]/50 flex items-center gap-2 transition-all duration-700 ${
                  badgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: "0ms" }}
              >
                <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-image-fill text-green-500 text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#2F3438]">
                    50 Images
                  </div>
                  <div className="text-[10px] text-[#6B7280]">
                    High-res photos
                  </div>
                </div>
              </div>

              <div
                className={`bg-white/90 backdrop-blur-sm rounded-xl shadow-lg px-3 py-2 border border-[#E5E7EB]/50 flex items-center gap-2 transition-all duration-700 ${
                  badgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: "150ms" }}
              >
                <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-video-fill text-red-500 text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#2F3438]">
                    12 Videos
                  </div>
                  <div className="text-[10px] text-[#6B7280]">
                    Reels & walkthroughs
                  </div>
                </div>
              </div>

              <div
                className={`bg-white/90 backdrop-blur-sm rounded-xl shadow-lg px-3 py-2 border border-[#E5E7EB]/50 flex items-center gap-2 transition-all duration-700 ${
                  badgesVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: "300ms" }}
              >
                <div className="w-7 h-7 bg-[#00BFF3]/20 rounded-lg flex items-center justify-center">
                  <i className="ri-home-smile-fill text-[#00BFF3] text-sm"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#2F3438]">
                    4 Home Tours
                  </div>
                  <div className="text-[10px] text-[#6B7280]">
                    Interactive tours
                  </div>
                </div>
              </div>
            </div>

            {/* Designer image */}
            <div className="absolute -bottom-4 left-0 sm:left-4 lg:left-8 z-20">
              <img
                src="https://public.readdy.ai/ai/img_res/7b7a6fcd-872c-4e72-a0d9-669ce113a179.png"
                alt="Designer updating profile"
                className="w-44 h-56 sm:w-56 sm:h-72 lg:w-64 lg:h-80 object-contain object-bottom drop-shadow-2xl scale-x-[-1]"
              />
            </div>

            {/* Phone mockup */}
            <div className="relative z-10 ml-auto mr-0 sm:mr-4 lg:mr-0">
              <div className="relative w-[220px] sm:w-[260px] lg:w-[300px]">
                <div className="bg-gradient-to-b from-[#E8E8E8] via-[#D4D4D4] to-[#C0C0C0] rounded-[36px] p-2 shadow-2xl">
                  <div className="bg-gradient-to-b from-[#F5F5F5] to-[#E0E0E0] rounded-[30px] overflow-hidden">
                    {/* Header strip */}
                    <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D4D4D4] h-5 flex items-center justify-center relative">
                      <div className="absolute w-20 h-4 bg-gradient-to-b from-[#E8E8E8] to-[#D4D4D4] rounded-b-xl"></div>
                      <div className="w-14 h-3 bg-[#2F3438] rounded-full"></div>
                    </div>

                    {/* Profile card */}
                    <div className="bg-white">
                      <div className="bg-gradient-to-br from-[#35C5F0]/15 to-white p-2.5">
                        <div className="flex items-center gap-2">
                          <img
                            src="https://readdy.ai/api/search-image?query=professional%20indian%20interior%20designer%20architect%20male%20female%20team%20working%20together%20modern%20studio%20confident%20experts&width=120&height=120&seq=hero-profile-main&orientation=squarish"
                            alt="Designer Profile"
                            className="w-10 h-10 rounded-full border-2 border-white shadow-lg object-cover flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <h3 className="text-[10px] font-bold text-[#2F3438] truncate">
                                Priya Sharma Studio
                              </h3>
                              <i className="ri-verified-badge-fill text-[#35C5F0] text-sm"></i>
                            </div>
                            <p className="text-[#6B7280] text-[8px]">
                              Interior Designer • Mumbai
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button className="w-6 h-6 bg-[#35C5F0]/10 rounded-full flex items-center justify-center hover:bg-[#35C5F0]/20 transition-colors cursor-pointer">
                              <i className="ri-phone-fill text-[#35C5F0] text-[10px]"></i>
                            </button>
                            <button className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors cursor-pointer">
                              <i className="ri-whatsapp-fill text-green-600 text-[10px]"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Filter bar */}
                      <div
                        ref={dropdownRef}
                        className="px-2 py-1.5 bg-[#F9FAFB] border-y border-[#E5E7EB]"
                      >
                        <div className="flex flex-wrap gap-1">
                          {/* Property Type */}
                          <div className="relative">
                            <button
                              onClick={() => toggleDropdown("propertyType")}
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 bg-white border rounded-full text-[7px] cursor-pointer transition-all ${
                                dropdowns.propertyType
                                  ? "border-[#35C5F0] shadow-sm"
                                  : "border-[#E5E7EB] hover:border-[#35C5F0]/50"
                              }`}
                            >
                              <i className="ri-home-4-line text-[#35C5F0] text-[8px]"></i>
                              <span className="text-[#2F3438] max-w-[45px] truncate">
                                {selectedFilters.propertyType}
                              </span>
                              <i
                                className={`ri-arrow-down-s-line text-[#6B7280] text-[8px] transition-transform ${
                                  dropdowns.propertyType ? "rotate-180" : ""
                                }`}
                              ></i>
                            </button>
                            {dropdowns.propertyType && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-50 min-w-[90px] py-1">
                                {filterOptions.propertyType.map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => selectOption("propertyType", option)}
                                    className="w-full text-left px-2 py-1 text-[8px] text-[#2F3438] hover:bg-[#35C5F0]/10 cursor-pointer transition-colors"
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Design Style */}
                          <div className="relative">
                            <button
                              onClick={() => toggleDropdown("designStyle")}
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 bg-white border rounded-full text-[7px] cursor-pointer transition-all ${
                                dropdowns.designStyle
                                  ? "border-[#35C5F0] shadow-sm"
                                  : "border-[#E5E7EB] hover:border-[#35C5F0]/50"
                              }`}
                            >
                              <i className="ri-palette-line text-[#35C5F0] text-[8px]"></i>
                              <span className="text-[#2F3438] max-w-[45px] truncate">
                                {selectedFilters.designStyle}
                              </span>
                              <i
                                className={`ri-arrow-down-s-line text-[#6B7280] text-[8px] transition-transform ${
                                  dropdowns.designStyle ? "rotate-180" : ""
                                }`}
                              ></i>
                            </button>
                            {dropdowns.designStyle && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-50 min-w-[90px] py-1">
                                {filterOptions.designStyle.map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => selectOption("designStyle", option)}
                                    className="w-full text-left px-2 py-1 text-[8px] text-[#2F3438] hover:bg-[#35C5F0]/10 cursor-pointer transition-colors"
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Budget */}
                          <div className="relative">
                            <button
                              onClick={() => toggleDropdown("budget")}
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 bg-white border rounded-full text-[7px] cursor-pointer transition-all ${
                                dropdowns.budget
                                  ? "border-[#35C5F0] shadow-sm"
                                  : "border-[#E5E7EB] hover:border-[#35C5F0]/50"
                              }`}
                            >
                              <i className="ri-money-rupee-circle-line text-[#35C5F0] text-[8px]"></i>
                              <span className="text-[#2F3438] max-w-[40px] truncate">
                                {selectedFilters.budget}
                              </span>
                              <i
                                className={`ri-arrow-down-s-line text-[#6B7280] text-[8px] transition-transform ${
                                  dropdowns.budget ? "rotate-180" : ""
                                }`}
                              ></i>
                            </button>
                            {dropdowns.budget && (
                              <div className="absolute top-full left-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-50 min-w-[90px] py-1">
                                {filterOptions.budget.map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => selectOption("budget", option)}
                                    className="w-full text-left px-2 py-1 text-[8px] text-[#2F3438] hover:bg-[#35C5F0]/10 cursor-pointer transition-colors"
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Area */}
                          <div className="relative">
                            <button
                              onClick={() => toggleDropdown("area")}
                              className={`flex items-center gap-0.5 px-1.5 py-0.5 bg-white border rounded-full text-[7px] cursor-pointer transition-all ${
                                dropdowns.area
                                  ? "border-[#35C5F0] shadow-sm"
                                  : "border-[#E5E7EB] hover:border-[#35C5F0]/50"
                              }`}
                            >
                              <i className="ri-ruler-line text-[#35C5F0] text-[8px]"></i>
                              <span className="text-[#2F3438] max-w-[35px] truncate">
                                {selectedFilters.area}
                              </span>
                              <i
                                className={`ri-arrow-down-s-line text-[#6B7280] text-[8px] transition-transform ${
                                  dropdowns.area ? "rotate-180" : ""
                                }`}
                              ></i>
                            </button>
                            {dropdowns.area && (
                              <div className="absolute top-full right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-xl z-50 min-w-[100px] py-1">
                                {filterOptions.area.map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => selectOption("area", option)}
                                    className="w-full text-left px-2 py-1 text-[8px] text-[#2F3438] hover:bg-[#35C5F0]/10 cursor-pointer transition-colors"
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Scrollable content */}
                      <div
                        ref={scrollContainerRef}
                        className="p-2 bg-white overflow-hidden"
                        style={{ maxHeight: "400px" }}
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                      >
                        <div
                          className="transition-transform duration-100 ease-linear"
                          style={{ transform: `translateY(-${scrollPosition}px)` }}
                        >
                          {/* Home Tours */}
                          <div className="mb-2.5">
                            <h4 className="text-[9px] font-semibold text-[#2F3438] mb-1.5">
                              Home Tours
                            </h4>
                            <div className="rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                              <div className="aspect-[16/9] relative">
                                <img
                                  src="https://readdy.ai/api/search-image?query=luxury%20modern%20living%20room%20interior%20design%20spacious%20open%20plan%20warm%20lighting%20elegant%20furniture%20contemporary%20indian%20home%20beautiful%20decor%20professional%20real%20estate%20photography&width=400&height=225&seq=home-tour-interior-1&orientation=landscape"
                                  alt="Home Tour"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
                                  <i className="ri-article-fill text-[#35C5F0] text-sm"></i>
                                </div>
                                <div className="absolute bottom-1.5 left-1.5 right-1.5">
                                  <div className="text-[8px] font-semibold text-white leading-tight">
                                    1,850 sq. ft. Gachibowli Tech Park Residence -
                                    Smart Home
                                  </div>
                                  <div className="text-[7px] text-white/80">
                                    Gachibowli, Hyderabad • 1,850 sq. ft.
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Photos & Videos */}
                          <div className="mb-2.5">
                            <h4 className="text-[9px] font-semibold text-[#2F3438] mb-1.5">
                              Photos & Videos
                            </h4>
                            <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=bright%20airy%20modern%20indian%20living%20room%20interior%20design%20soft%20beige%20cream%20colors%20natural%20sunlight%20cozy%20sofa%20elegant%20minimalist%20decor%20professional%20photography&width=200&height=200&seq=hero-room-living-bright&orientation=squarish"
                                  alt="Living Room"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=serene%20minimalist%20bedroom%20interior%20design%20white%20walls%20light%20wood%20natural%20linen%20bedding%20soft%20morning%20sunlight%20peaceful%20atmosphere%20professional%20photography&width=200&height=200&seq=hero-room-bedroom-serene&orientation=squarish"
                                  alt="Bedroom"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute bottom-1 left-1 bg-red-500 text-white px-1.5 py-0.5 rounded text-[6px] flex items-center gap-0.5">
                                  <i className="ri-play-fill text-[7px]"></i>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-1.5 mb-1.5">
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=clean%20modern%20kitchen%20interior%20design%20white%20cabinets%20light%20marble%20countertops%20natural%20wood%20accents%20bright%20sunlight%20minimalist%20professional%20photography&width=200&height=200&seq=hero-room-kitchen-clean&orientation=squarish"
                                  alt="Kitchen"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=elegant%20dining%20room%20interior%20design%20light%20neutral%20tones%20white%20walls%20wooden%20table%20natural%20sunlight%20simple%20minimalist%20professional%20photography&width=200&height=200&seq=hero-room-dining-elegant&orientation=squarish"
                                  alt="Dining Room"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute bottom-1 left-1 bg-red-500 text-white px-1.5 py-0.5 rounded text-[6px] flex items-center gap-0.5">
                                  <i className="ri-play-fill text-[7px]"></i>
                                </div>
                              </div>
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=spa%20bathroom%20interior%20design%20soft%20white%20marble%20light%20beige%20tones%20natural%20sunlight%20through%20window%20peaceful%20serene%20minimalist%20professional%20photography&width=200&height=200&seq=hero-room-bathroom-spa&orientation=squarish"
                                  alt="Bathroom"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-1.5">
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=modern%20home%20office%20interior%20design%20clean%20white%20desk%20natural%20light%20plants%20minimalist%20scandinavian%20style%20professional%20photography&width=200&height=200&seq=hero-room-office-modern&orientation=squarish"
                                  alt="Home Office"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute bottom-1 left-1 bg-red-500 text-white px-1.5 py-0.5 rounded text-[6px] flex items-center gap-0.5">
                                  <i className="ri-play-fill text-[7px]"></i>
                                </div>
                              </div>
                              <div className="aspect-square rounded-lg overflow-hidden bg-[#F5F5F5] relative group cursor-pointer">
                                <img
                                  src="https://readdy.ai/api/search-image?query=cozy%20reading%20nook%20interior%20design%20warm%20lighting%20comfortable%20armchair%20bookshelves%20soft%20textures%20inviting%20atmosphere%20professional%20photography&width=200&height=200&seq=hero-room-nook-cozy&orientation=squarish"
                                  alt="Reading Nook"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Know Hows */}
                          <div>
                            <h4 className="text-[9px] font-semibold text-[#2F3438] mb-0.5">
                              Know Hows
                            </h4>
                            <p className="text-[7px] text-[#6B7280] mb-1.5">
                              Tips & Advice
                            </p>
                            <div className="space-y-1.5">
                              <div className="bg-[#F9FAFB] rounded-lg p-2 flex items-center gap-2 cursor-pointer hover:bg-[#F0F9FF] transition-colors">
                                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                                  <img
                                    src="https://readdy.ai/api/search-image?query=interior%20design%20color%20palette%20swatches%20fabric%20samples%20mood%20board%20professional%20styling%20tips%20warm%20neutral%20tones&width=100&height=100&seq=tip-color-palette&orientation=squarish"
                                    alt="Color Tips"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[8px] font-semibold text-[#2F3438] truncate">
                                    5 Color Trends for 2024
                                  </div>
                                  <div className="text-[7px] text-[#6B7280]">
                                    3 min read
                                  </div>
                                </div>
                                <i className="ri-arrow-right-s-line text-[#ABB2B9] text-xs"></i>
                              </div>

                              <div className="bg-[#F9FAFB] rounded-lg p-2 flex items-center gap-2 cursor-pointer hover:bg-[#F0F9FF] transition-colors">
                                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                                  <img
                                    src="https://readdy.ai/api/search-image?query=small%20space%20interior%20design%20clever%20storage%20solutions%20compact%20apartment%20living%20smart%20furniture%20arrangement%20professional%20tips&width=100&height=100&seq=tip-small-space&orientation=squarish"
                                    alt="Space Tips"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[8px] font-semibold text-[#2F3438] truncate">
                                    Maximize Small Spaces
                                  </div>
                                  <div className="text-[7px] text-[#6B7280]">
                                    5 min read
                                  </div>
                                </div>
                                <i className="ri-arrow-right-s-line text-[#ABB2B9] text-xs"></i>
                              </div>

                              <div className="bg-[#F9FAFB] rounded-lg p-2 flex items-center gap-2 cursor-pointer hover:bg-[#F0F9FF] transition-colors">
                                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                                  <img
                                    src="https://readdy.ai/api/search-image?query=sustainable%20eco%20friendly%20interior%20design%20natural%20materials%20bamboo%20recycled%20decor%20green%20living%20plants%20professional%20tips&width=100&height=100&seq=tip-sustainable&orientation=squarish"
                                    alt="Sustainable Tips"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[8px] font-semibold text-[#2F3438] truncate">
                                    Sustainable Design Guide
                                  </div>
                                  <div className="text-[7px] text-[#6B7280]">
                                    4 min read
                                  </div>
                                </div>
                                <i className="ri-arrow-right-s-line text-[#ABB2B9] text-xs"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="bg-[#F9FAFB] px-2 py-1.5 grid grid-cols-4 gap-1 border-t border-[#E5E7EB]">
                        <div className="text-center">
                          <div className="text-xs font-bold text-[#2F3438]">
                            45
                          </div>
                          <div className="text-[7px] text-[#6B7280]">
                            Projects
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs font-bold text-[#2F3438]">
                            2.3K
                          </div>
                          <div className="text-[7px] text-[#6B7280]">
                            Followers
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs font-bold text-[#2F3438]">
                            127
                          </div>
                          <div className="text-[7px] text-[#6B7280]">
                            Reviews
                          </div>
                        </div>
                        <div className="text-center relative">
                          <div className="text-xs font-bold text-[#00BFF3]">
                            18
                          </div>
                          <div className="text-[7px] text-[#6B7280]">Leads</div>
                        </div>
                      </div>
                    </div>

                    {/* Phone bottom bar */}
                    <div className="bg-white h-3 flex items-center justify-center">
                      <div className="w-20 h-1 bg-[#C0C0C0] rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notification bubble */}
            <div className="absolute -bottom-20 right-0 sm:right-4 z-20">
              <div className="relative">
                <div className="absolute inset-0 bg-[#00BFF3] blur-xl opacity-40 rounded-2xl scale-110"></div>
                <div className="absolute -top-3 right-8 w-4 h-4 bg-[#00BFF3] rotate-45 rounded-sm z-10"></div>
                <div className="relative bg-[#00BFF3] text-white px-5 py-3 rounded-2xl shadow-xl min-w-[180px] animate-pulse">
                  <div className="relative flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                        <i className="ri-notification-3-fill text-xl"></i>
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-[#00BFF3] flex items-center justify-center">
                        <span className="text-white text-[10px] font-bold">5</span>
                      </div>
                    </div>
                    <div className="h-10 overflow-hidden flex-1">
                      {leads.map((lead, index) => (
                        <div
                          key={index}
                          className={`transition-all duration-500 ${
                            index === currentLeadIndex
                              ? "opacity-100 translate-y-0"
                              : index ===
                                (currentLeadIndex - 1 + leads.length) %
                                  leads.length
                              ? "opacity-0 -translate-y-full absolute"
                              : "opacity-0 translate-y-full absolute"
                          }`}
                        >
                          <div className="text-sm font-bold">New Lead!</div>
                          <div className="text-xs text-white/90">
                            {lead.name} {lead.action}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
